import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import "leaflet"

export default class extends Controller {
  static values = { url: String }
  static targets = ["map", "overlay", "empty"]

  async connect() {
    if (!this.urlValue) return
    await this.initializeMap()
  }

  async initializeMap() {
    const map = L.map(this.mapTarget).setView([47.5, 19.0], 6)
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map)
    const markers = L.featureGroup().addTo(map)

    const response = await get(this.urlValue, { responseKind: "json" })

    if (!response.ok) {
      this.showEmpty()
      return
    }

    const coordinates = await response.json
    const sortedCoords = Array.isArray(coordinates)
      ? [...coordinates].sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0))
      : []
    const points = sortedCoords.map(c => [c.latitude, c.longitude])

    if (points.length === 0) {
      this.showEmpty()
      return
    }

    const polyline = L.polyline(points, { color: 'red', weight: 3, opacity: 0.8 }).addTo(map)

    const startIcon = L.icon({
      iconUrl: '/leaflet/marker-icon.png',
      iconRetinaUrl: '/leaflet/marker-icon-2x.png',
      shadowUrl: '/leaflet/marker-shadow.png',
      iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
    })
    const greenIcon = L.icon({
      iconUrl: '/leaflet/marker-icon-green.png',
      iconRetinaUrl: '/leaflet/marker-icon-2x-green.png',
      shadowUrl: '/leaflet/marker-shadow.png',
      iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
    })

    markers.addLayer(L.marker(points[0], { icon: startIcon }).bindTooltip("Start", { permanent: true, direction: 'right', offset: [10, 0], className: 'patrol-label' }))
    markers.addLayer(L.marker(points[points.length - 1], { icon: greenIcon }).bindTooltip("End", { permanent: true, direction: 'right', offset: [10, 0], className: 'patrol-label' }))

    if (L.polylineDecorator) {
      try {
        L.polylineDecorator(points, {
          patterns: [{
            offset: '5%', repeat: '5%',
            symbol: L.Symbol.arrowHead({ pixelSize: 12, headAngle: 30, polygon: true, pathOptions: { color: '#000000', weight: 1, opacity: 1.0, fillOpacity: 1.0, fillColor: '#ffffff' } })
          }]
        }).addTo(map)
      } catch (e) { /* decorator not available */ }
    }

    for (let i = 1; i < sortedCoords.length; i++) {
      const prev = sortedCoords[i - 1]
      const curr = sortedCoords[i]
      if (prev.checkpoint_id && prev.checkpoint_id !== curr.checkpoint_id) {
        const cm = L.circleMarker([prev.latitude, prev.longitude], {
          radius: 5, color: '#0d6efd', weight: 2, fillColor: '#ffffff', fillOpacity: 1.0
        })
        cm.bindTooltip(prev.checkpoint?.name || `Checkpoint #${prev.checkpoint_id}`, { permanent: true, direction: 'top', offset: [0, -10], className: 'patrol-label' })
        markers.addLayer(cm)
      }
    }

    const bounds = polyline.getBounds()
    const waitMoveEnd = new Promise(resolve => map.once('moveend', resolve))
    map.fitBounds(bounds)
    await Promise.race([waitMoveEnd, new Promise(r => setTimeout(r, 1500))])

    this.mapTarget.style.opacity = '1'
    this.hideOverlay()
  }

  showEmpty() {
    this.hideOverlay()
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.remove('d-none')
      this.emptyTarget.classList.add('d-flex')
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add('d-none')
  }
}
